import { DateOnly } from '@if/core/date'
import { Banner, ControlledNumberField, ControlledSelect, FieldGrid, FormMethods, useFormDialog } from '@if/mui'
import { Stack } from '@mui/material'
import {
  CalendarSimulationAction,
  ECalendarSimulationAction,
  SignedCalendarSimulation,
} from '@src/types/loan/loan-simulation'
import { CircleDollarSign } from 'lucide-react'
import { JSX, useMemo } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCalendarPreviewConfirm } from '../LoanCalendarActionPreview/LoanCalendarActionPreview-confirm'
import {
  useLoanCalendarActionPreview,
  useSettledValue,
} from '../LoanCalendarActionPreview/LoanCalendarActionPreview-recompute'
import LoanCalendarActionPreviewBody from '../LoanCalendarActionPreview/LoanCalendarActionPreviewBody'
import { useValidPaymentOperationsForCalendarShift } from '../LoanMoveCalendar/LoanMoveCalendar-hooks'
import { ChangeRegularPmtSchemaType, useChangeRegularPmtSchema } from './LoanCalendarChangeRegularPmt-schema'

interface LoanCalendarChangeRegularPmtBodyProps {
  form: FormMethods<ChangeRegularPmtSchemaType>
  initialSignedSimulation: SignedCalendarSimulation
  paymentOptions: JSX.Element[]
  disabled: boolean
}

const LoanCalendarChangeRegularPmtBody = ({
  form,
  initialSignedSimulation,
  paymentOptions,
  disabled,
}: LoanCalendarChangeRegularPmtBodyProps) => {
  const { t } = useTranslation()
  const { control } = form

  const previewedSimulation = useWatch({ control, name: 'previewedSimulation' })
  const amount = useWatch({ control, name: 'amount' })
  const fromDate = useWatch({ control, name: 'fromDate' })
  const { isValid } = useFormState({ control })

  // The recompute follows the fields once they settle, and settling is what opens Confirm —
  // so what the agent is about to apply is always the calendar they are looking at.
  const settledAmount = useSettledValue(amount)
  const settledFromDate = useSettledValue(fromDate)

  const action = useMemo<CalendarSimulationAction | null>(
    () =>
      isValid && settledAmount != null && settledFromDate
        ? { actionType: ECalendarSimulationAction.ChangeRegularPmt, amount: settledAmount, fromDate: settledFromDate }
        : null,
    [isValid, settledAmount, settledFromDate],
  )

  const error = useLoanCalendarActionPreview({ form, initialSignedSimulation, action })

  return (
    <Stack spacing={2}>
      <LoanCalendarActionPreviewBody
        simulationBefore={initialSignedSimulation.simulation}
        preview={previewedSimulation}
        recomputing={isValid && previewedSimulation === null}
        actionDate={fromDate}
        error={error}
      />

      <FieldGrid columns={{ xs: 1, sm: 2 }}>
        <ControlledNumberField
          control={control}
          name="amount"
          label={t('loan.payment.changeRegularPmtDialog.amount')}
          disabled={disabled}
        />
        <ControlledSelect
          name="fromDate"
          control={control}
          options={paymentOptions}
          parseValue={(value: string | null) => (value ? new DateOnly(value) : null)}
          label={t('loan.payment.changeRegularPmtDialog.fromDate')}
          // Whether the new amount is a real change is only decidable once there is a date to
          // read the current one at, so picking one re-decides the field beside it.
          onChange={() => void form.trigger('amount')}
          disableEmptyOption
          disabled={disabled}
        />
      </FieldGrid>

      <Banner severity="warning">{t('loan.payment.changeRegularPmtDialog.warning')}</Banner>
    </Stack>
  )
}

export const useLoanCalendarChangeRegularPmtDialog = (
  initialSignedSimulation: SignedCalendarSimulation,
  isLockedByOther: boolean,
) => {
  const { t } = useTranslation()
  const schema = useChangeRegularPmtSchema(initialSignedSimulation.simulation)
  const paymentOptions = useValidPaymentOperationsForCalendarShift(initialSignedSimulation.simulation)
  const confirm = useCalendarPreviewConfirm(isLockedByOther)

  return useFormDialog<ChangeRegularPmtSchemaType, void, SignedCalendarSimulation>({
    icon: CircleDollarSign,
    title: t('loan.payment.changeRegularPmtDialog.title'),
    confirmLabel: t('common.confirm'),
    maxWidth: 'sm',
    fullWidth: true,
    schema,
    // Confirming stages a pending change on the page, it saves nothing — the page says so.
    successMessage: false,
    ...confirm,
    content: ({ form }) => (
      <LoanCalendarChangeRegularPmtBody
        form={form}
        initialSignedSimulation={initialSignedSimulation}
        paymentOptions={paymentOptions}
        disabled={isLockedByOther}
      />
    ),
    onSubmit: (values) => values.previewedSimulation ?? false,
  })
}
